import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Store, Settings2, Shield, FileText,
  Megaphone, Ban, Brain, Sparkles, Film, Bot, Palette,
} from 'lucide-react';
import { ARENA } from '../../lib/classTheme';
import useTheme from '../../lib/useTheme';
import { ThemePicker } from '../../components/ThemeSwitcher';
import { ArenaTitle, Laurel } from '../../components/ArenaFrame';

import Overview from './Overview';
import AdminUsers from './AdminUsers';
import AdminShops from './AdminShops';
import AdminEconomy from './AdminEconomy';
import AdminModeration from './AdminModeration';
import AdminBans from './AdminBans';
import AdminBroadcast from './AdminBroadcast';
import AdminAudit from './AdminAudit';
import AdminTrivia from './AdminTrivia';
import AdminGeneration from './AdminGeneration';
import AdminVideo from './AdminVideo';
import AdminNPC from './AdminNPC';
import AdminInvites from './AdminInvites';

const TABS = [
  { to: '',           icon: LayoutDashboard, l: 'Overview' },
  { to: 'generation', icon: Sparkles,        l: 'AI Studio' },
  { to: 'video',      icon: Film,            l: 'Video' },
  { to: 'npc',        icon: Bot,             l: 'NPCs' },
  { to: 'users',      icon: Users,           l: 'Users' },
  { to: 'shops',      icon: Store,           l: 'Shops' },
  { to: 'invites',    icon: Store,           l: 'Leads' },
  { to: 'economy',    icon: Settings2,       l: 'Economy' },
  { to: 'trivia',     icon: Brain,           l: 'Trivia' },
  { to: 'moderation', icon: FileText,        l: 'Content' },
  { to: 'bans',       icon: Ban,             l: 'IP bans' },
  { to: 'broadcast',  icon: Megaphone,       l: 'Broadcast' },
  { to: 'theme',      icon: Palette,         l: 'Theme' },
  { to: 'audit',      icon: Shield,          l: 'Audit' },
];

export default function Admin() {
  const { L, meta } = useTheme();

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena arena-arches p-4 flex items-center gap-3">
        <div className="w-11 h-11 grid place-items-center shrink-0"
          style={{
            borderRadius: 'var(--radius)',
            background: 'var(--accent-soft)',
            border: `1px solid ${ARENA.gold}`,
          }}>
          <Shield size={20} style={{ color: ARENA.arenaText }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Laurel size={18} color={ARENA.arenaText} />
            <h1 className="font-display text-lg font-extrabold neon-glow-text"
              style={{ color: ARENA.arenaText }}>
              {L('senate')}
            </h1>
          </div>
          <p className="text-[11px] font-semibold" style={{ color: ARENA.arenaTextSoft }}>
            Wall of Freedom · full platform control · {meta.icon} {meta.name}
          </p>
        </div>
      </div>

      {/* ═══ TABS ═══ */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar p-1 sticky top-14 z-20"
        style={{
          borderRadius: 'var(--radius)',
          background: 'var(--surface-grad)',
          border: '1px solid var(--border)',
          backdropFilter: 'blur(12px)',
        }}>
        {TABS.map(t => (
          <NavLink key={t.to} to={`/admin/${t.to}`} end={t.to === ''}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold whitespace-nowrap transition"
            style={({ isActive }) => ({
              borderRadius: 'var(--radius-sm)',
              background: isActive ? ARENA.goldFrame : 'transparent',
              color: isActive ? (meta.mode === 'dark' ? '#04121a' : '#fff') : 'var(--text-muted)',
              boxShadow: isActive ? 'var(--shadow-glow)' : 'none',
            })}>
            <t.icon size={13} /> {t.l}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route index element={<Overview />} />
        <Route path="generation" element={<AdminGeneration />} />
        <Route path="video" element={<AdminVideo />} />
        <Route path="npc" element={<AdminNPC />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="shops" element={<AdminShops />} />
        <Route path="invites" element={<AdminInvites />} />
        <Route path="economy" element={<AdminEconomy />} />
        <Route path="trivia" element={<AdminTrivia />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="bans" element={<AdminBans />} />
        <Route path="broadcast" element={<AdminBroadcast />} />
        <Route path="theme" element={<AdminTheme />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </div>
  );
}

/* ============================================================
   THEME TAB
   ============================================================ */
function AdminTheme() {
  const { theme, meta, isNeon } = useTheme();

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <ArenaTitle title="Platform theme" latin="Aspectus"
          sub="Switch the entire visual identity — structure stays identical" />
        <ThemePicker />
      </div>

      <div className="card p-5">
        <ArenaTitle title="Live preview" latin="Exemplum" />
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary btn-sm">Primary</button>
            <button className="btn-ghost btn-sm">Ghost</button>
            <button className="btn-dark btn-sm">Dark</button>
            <button className="btn-mint btn-sm">Success</button>
            <button className="btn-blood btn-sm">Danger</button>
            <button className="btn-grape btn-sm">Special</button>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <span className="chip-gold">Gold chip</span>
            <span className="chip-brand">Brand</span>
            <span className="chip-mint">Success</span>
            <span className="chip-grape">Epic</span>
            <span className="chip-blood">Danger</span>
            <span className="chip-ink">Neutral</span>
          </div>

          <input className="input" placeholder="Input field preview…" />

          <div>
            <p className="label">Progress bar</p>
            <div className="stat-bar">
              <div className="stat-fill" style={{ width: '68%', background: ARENA.goldFrame }} />
            </div>
          </div>

          <div className="card-arena p-4">
            <p className="font-display font-extrabold neon-glow-text"
              style={{ color: ARENA.arenaText }}>
              Arena surface
            </p>
            <p className="text-xs mt-1" style={{ color: ARENA.arenaTextSoft }}>
              Dark battle background used in fights and races
            </p>
          </div>

          <div className="divider-laurel"><span>{isNeon ? '⚡' : '⚔️'}</span></div>

          <p className="font-display text-2xl font-black shimmer-gold text-center">
            Wall of Freedom
          </p>
        </div>
      </div>

      <div className="card p-5">
        <ArenaTitle title="Design tokens" latin="Elementa" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ['--accent', 'Accent'],
            ['--accent-light', 'Accent light'],
            ['--accent-dark', 'Accent dark'],
            ['--bg', 'Background'],
            ['--surface', 'Surface'],
            ['--border', 'Border'],
            ['--text', 'Text'],
            ['--danger', 'Danger'],
          ].map(([v, l]) => (
            <div key={v} className="rounded-xl overflow-hidden"
              style={{ border: '1px solid var(--border)' }}>
              <div className="h-10" style={{ background: `var(${v})` }} />
              <div className="p-1.5">
                <p className="text-[10px] font-display font-bold">{l}</p>
                <p className="text-[8px] font-mono" style={{ color: 'var(--text-muted)' }}>{v}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 rounded-xl" style={{ background: 'var(--surface-alt)' }}>
          <p className="text-[11px] font-bold" style={{ color: 'var(--text-soft)' }}>
            💡 Theme is stored per-device in <code>localStorage.wof_theme</code>.
            Hero, pet, car and item artwork keeps the same class colors in both themes.
          </p>
        </div>
      </div>
    </div>
  );
}